import { useTheme } from '../../hooks/useTheme'
import type { ReactNode, ButtonHTMLAttributes } from 'react'

type ButtonVariant = 
  | 'primary'      // Main action (accent blue)
  | 'secondary'    // Secondary action (subtle)
  | 'success'      // Success/confirm actions (green)
  | 'warning'      // Warning actions (yellow)
  | 'danger'       // Destructive actions (red)
  | 'brand'        // Brand blue
  | 'ghost'        // No background, just text/border

type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  children: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  isActive?: boolean  // For toggle/segmented buttons
  fullWidth?: boolean
  className?: string
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isActive = false,
  fullWidth = false,
  className = '',
  ...props
}: ButtonProps) {
  const theme = useTheme()
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg',
    icon: 'w-11 h-11 p-0', // Square icon button
  }
  
  const getVariantStyles = () => {
    const baseStyles = {
      transition: 'all 0.2s',
      cursor: props.disabled ? 'not-allowed' : 'pointer',
      opacity: props.disabled ? 0.5 : 1,
    }
    
    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: isActive 
            ? 'rgba(0, 136, 204, 0.2)' 
            : 'rgba(0, 136, 204, 0.15)',
          border: isActive
            ? '2px solid rgba(0, 136, 204, 0.5)'
            : '1px solid rgba(0, 136, 204, 0.3)',
          color: theme.accent.text,
        }
      
      case 'secondary':
        return {
          ...baseStyles,
          backgroundColor: isActive ? theme.cardHoverBg : theme.cardBg,
          border: `1px solid ${theme.cardBorder}`,
          color: theme.textColor,
        }
      
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: isActive 
            ? 'rgba(20, 120, 60, 0.85)' 
            : 'rgba(22, 101, 52, 0.9)',
          border: 'none',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(22, 101, 52, 0.2)',
        }
      
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: isActive 
            ? 'rgba(234, 179, 8, 0.2)' 
            : 'rgba(234, 179, 8, 0.15)',
          border: isActive
            ? '1px solid rgba(234, 179, 8, 0.5)'
            : '1px solid rgba(234, 179, 8, 0.3)',
          color: '#eab308',
        }
      
      case 'danger':
        return {
          ...baseStyles,
          backgroundColor: isActive 
            ? 'rgba(239, 68, 68, 0.2)' 
            : 'rgba(239, 68, 68, 0.15)',
          border: isActive
            ? '1px solid rgba(239, 68, 68, 0.5)'
            : '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444',
        }
      
      case 'brand':
        return {
          ...baseStyles,
          backgroundColor: isActive ? theme.brand.dark : theme.brand.medium,
          border: isActive
            ? `2px solid ${theme.brand.border}`
            : `1px solid ${theme.brand.border}`,
          color: theme.text.secondary,
        }
      
      case 'ghost':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          border: 'none',
          color: theme.textColor,
        }
      
      default:
        return baseStyles
    }
  }
  
  const iconButtonStyles = size === 'icon' ? {
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } : {}
  
  return (
    <button
      className={`
        rounded-xl font-semibold transition-all
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${props.disabled ? '' : 'hover:opacity-80 active:scale-[0.98]'}
        ${className}
      `}
      style={{
        ...getVariantStyles(),
        ...iconButtonStyles,
      }}
      {...props}
    >
      {children}
    </button>
  )
}
